import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Typography, message } from 'antd';
import { ArrowLeftOutlined, EnvironmentOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Map, { MapRef, Marker, NavigationControl } from 'react-map-gl';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../style/Measurements.css';

const { Text, Title } = Typography;

interface Measurement {
    id: number;
    measurementCode: string;
    createdAt: string;
    geom?: { type: string; coordinates: number[] } | null;
    area?: { id: number; name: string } | null;
    device?: { id: number; label: string } | null;
}

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

const MeasurementEditLocation: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const mapRef = useRef<MapRef>(null);
    
    const [measurement, setMeasurement] = useState<Measurement | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
    const [viewState, setViewState] = useState({
        longitude: -0.1278,
        latitude: 51.5074,
        zoom: 13,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    const measurementId = parseInt(id || '0', 10);

    const handleSearch = useCallback(async (query: string) => {
        if (!query || query.length < 3) {
            setSearchResults([]);
            return;
        }

        setSearching(true);
        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5`
            );
            const data = await response.json();
            setSearchResults(data.features || []);
        } catch (error) {
            console.error('Search failed', error);
            message.error('Failed to search location');
        } finally {
            setSearching(false);
        }
    }, []);

    const handleSelectLocation = useCallback((feature: any) => {
        const [lng, lat] = feature.center;
        setMarkerPosition([lng, lat]);
        setViewState({ longitude: lng, latitude: lat, zoom: 15 });
        form.setFieldsValue({ latitude: lat, longitude: lng });
        setSearchQuery('');
        setSearchResults([]);
        message.success(`Location set to: ${feature.place_name}`);
    }, [form]);

    const fetchMeasurement = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.MEASUREMENT_DETAIL(measurementId), {
                withCredentials: true,
            });
            const data = res.data?.data || res.data;
            setMeasurement(data);

            // Set initial coordinates
            if (data.geom?.type === 'Point' && Array.isArray(data.geom.coordinates)) {
                const [lng, lat] = data.geom.coordinates;
                setMarkerPosition([lng, lat]);
                setViewState({ longitude: lng, latitude: lat, zoom: 15 });
                form.setFieldsValue({ latitude: lat, longitude: lng });
            }
        } catch (error) {
            console.error('Failed to load measurement', error);
            message.error('Failed to load measurement');
            navigate('/measurements');
        } finally {
            setLoading(false);
        }
    }, [measurementId, navigate, form]);

    useEffect(() => {
        fetchMeasurement();
    }, [fetchMeasurement]);

    const handleMapClick = useCallback((event: any) => {
        const { lng, lat } = event.lngLat;
        setMarkerPosition([lng, lat]);
        form.setFieldsValue({ latitude: lat, longitude: lng });
    }, [form]);

    const handleMarkerDragEnd = useCallback((event: any) => {
        const { lng, lat } = event.lngLat;
        setMarkerPosition([lng, lat]);
        form.setFieldsValue({ latitude: lat, longitude: lng });
    }, [form]);

    const handleCoordinatesChange = () => {
        const values = form.getFieldsValue();
        if (values.latitude !== undefined && values.longitude !== undefined) {
            setMarkerPosition([values.longitude, values.latitude]);
            setViewState({
                longitude: values.longitude,
                latitude: values.latitude,
                zoom: 15,
            });
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            await axios.patch(
                ENDPOINTS.MEASUREMENT_UPDATE_LOCATION(measurementId),
                {
                    latitude: values.latitude,
                    longitude: values.longitude,
                },
                { withCredentials: true }
            );

            message.success('GPS location updated successfully');
            navigate('/measurements');
        } catch (error: any) {
            console.error('Failed to update location', error);
            message.error(error.response?.data?.message || 'Failed to update GPS location');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div style={{ padding: 24 }}>Loading...</div>;
    }

    if (!measurement) {
        return <div style={{ padding: 24 }}>Measurement not found</div>;
    }

    return (
        <div className="measurements-page measurement-edit-location-page">
            <Row justify="start" align="middle" className="measurements-header">
                <Col>
                    <Space size="middle" align="center">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/measurements')}>
                            Back
                        </Button>
                        <Title level={3} style={{ margin: 0 }}>Edit GPS Location</Title>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} lg={8}>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Card size="small" className="measurement-edit-info-card" style={{ background: '#e6f7ff' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Measurement</Text>
                                <Text>{measurement.measurementCode}</Text>
                                {measurement.area && (
                                    <Text type="secondary">Area: {measurement.area.name}</Text>
                                )}
                                {measurement.device && (
                                    <Text type="secondary">Device: {measurement.device.label}</Text>
                                )}
                            </Space>
                        </Card>

                        <Card title="GPS Coordinates" className="measurement-edit-coordinates-card">
                            <Form form={form} layout="vertical">
                                <Form.Item
                                    label="Latitude"
                                    name="latitude"
                                    rules={[
                                        { required: true, message: 'Enter latitude' },
                                        { type: 'number', min: -90, max: 90, message: 'Must be between -90 and 90' }
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        precision={6}
                                        onChange={handleCoordinatesChange}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Longitude"
                                    name="longitude"
                                    rules={[
                                        { required: true, message: 'Enter longitude' },
                                        { type: 'number', min: -180, max: 180, message: 'Must be between -180 and 180' }
                                    ]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        precision={6}
                                        onChange={handleCoordinatesChange}
                                    />
                                </Form.Item>
                            </Form>
                        </Card>

                        <Card size="small" className="measurement-edit-help-card" style={{ background: '#fff3e0' }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong><EnvironmentOutlined /> How to use:</Text>
                                <Text style={{ fontSize: '12px' }}>• Click anywhere on the map to set location</Text>
                                <Text style={{ fontSize: '12px' }}>• Drag the marker to adjust position</Text>
                                <Text style={{ fontSize: '12px' }}>• Or enter coordinates manually above</Text>
                            </Space>
                        </Card>

                        <Button
                            className="measurement-edit-save-button"
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            loading={saving}
                            disabled={!markerPosition}
                            block
                        >
                            Save Location
                        </Button>
                    </Space>
                </Col>

                <Col xs={24} lg={16}>
                    <Card title="Map" className="measurements-map-card measurement-edit-map-card">
                        <div style={{ marginBottom: 12 }}>
                            <Select
                                className="measurement-edit-search"
                                showSearch
                                value={searchQuery}
                                placeholder="Search for a location (e.g., city, address, landmark)"
                                style={{ width: '100%' }}
                                defaultActiveFirstOption={false}
                                suffixIcon={<SearchOutlined />}
                                filterOption={false}
                                onSearch={handleSearch}
                                onChange={(value) => {
                                    const result = searchResults.find(r => r.id === value);
                                    if (result) {
                                        handleSelectLocation(result);
                                    }
                                }}
                                notFoundContent={searching ? 'Searching...' : searchQuery.length < 3 ? 'Type at least 3 characters to search' : 'No results found'}
                                options={searchResults.map(result => ({
                                    value: result.id,
                                    label: result.place_name,
                                }))}
                            />
                        </div>
                        <div className="measurements-map-wrapper">
                            <Map
                                ref={mapRef}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                initialViewState={viewState}
                                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                style={{ width: '100%', height: '100%' }}
                                onClick={handleMapClick}
                            >
                                {markerPosition && (
                                    <Marker
                                        longitude={markerPosition[0]}
                                        latitude={markerPosition[1]}
                                        anchor="center"
                                        draggable
                                        onDragEnd={handleMarkerDragEnd}
                                    >
                                        <div style={{
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '50%',
                                            background: '#1890ff',
                                            border: '3px solid white',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                            cursor: 'move',
                                        }} />
                                    </Marker>
                                )}
                            </Map>
                        </div>
                        {markerPosition && (
                            <div className="measurement-edit-current-position" style={{ marginTop: 12, padding: '12px', background: '#f0f2f5', borderRadius: '8px' }}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text strong>Current Position</Text>
                                    <Text>
                                        📍 {markerPosition[1].toFixed(6)}, {markerPosition[0].toFixed(6)}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        (Latitude, Longitude)
                                    </Text>
                                </Space>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default MeasurementEditLocation;
